import { useState, useRef, useEffect, type CSSProperties } from "react";
import { useIsMobile } from "./useIsMobile";

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const DOW = ["lun", "mar", "mer", "jeu", "ven", "sam", "dim"];

function parse(v?: string): Date | null {
  if (!v) return null;
  const [y, m, d] = v.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function iso(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function sameDay(a: Date | null, b: Date | null) {
  return !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
export function nextWeekday(weekday = 0, from = new Date()) {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const delta = (weekday - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + delta);
  return iso(d);
}
function formatFrLong(v?: string) {
  const d = parse(v);
  if (!d) return "";
  const wd = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"][d.getDay()];
  return `${wd} ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
// Compact label (no weekday) — the rail already shows the weekday, so the trigger
// can stay short and keep the toolbar on one line.
function formatFrShort(v?: string) {
  const d = parse(v);
  if (!d) return "";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function DatePicker({
  value,
  onChange,
  highlightWeekday = 0,
  placeholder = "Choisir une date",
  compact = false,
  fullWidth = false,
  style,
}: {
  value: string;
  onChange?: (v: string) => void;
  highlightWeekday?: number;
  placeholder?: string;
  compact?: boolean;
  fullWidth?: boolean;
  style?: CSSProperties;
}) {
  const isMobile = useIsMobile();
  const stretch = isMobile || fullWidth; // fill the column when the toolbar is stacked
  const selected = parse(value);
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<Date>(() => selected || today);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selected) setView(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  function shiftMonth(n: number) {
    setView(new Date(year, month + n, 1));
  }
  function choose(d: Date) {
    onChange && onChange(iso(d));
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: "relative", display: stretch ? "block" : "inline-block", ...style }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ display: "inline-flex", alignItems: "center", gap: "9px", width: stretch ? "100%" : undefined, justifyContent: "flex-start", boxSizing: "border-box", background: "var(--bg-raised)", border: `1px solid ${open ? "var(--accent)" : "var(--border-strong)"}`, borderRadius: "var(--radius-md)", padding: "9px 13px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: "var(--text-base)", color: "var(--text-primary)", boxShadow: open ? "0 0 0 var(--ring-width) var(--focus-ring)" : "none", transition: "border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)" }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></svg>
        <span style={{ color: value ? "var(--text-primary)" : "var(--text-faint)" }}>{value ? (compact ? formatFrShort(value) : formatFrLong(value)) : placeholder}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 2, transform: open ? "rotate(180deg)" : "none", transition: "transform var(--dur-fast) var(--ease-out)" }}><path d="m6 9 6 6 6-6" /></svg>
      </button>

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, zIndex: 60, width: "min(296px, calc(100vw - 24px))", maxWidth: "calc(100vw - 24px)", background: "var(--bg-raised)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-lg)", padding: "14px", animation: "adm-pop var(--dur-base) var(--ease-out)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <NavBtn dir="prev" onClick={() => shiftMonth(-1)} />
            <span style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: "var(--text-md)", color: "var(--text-primary)", textTransform: "capitalize" }}>{MONTHS[month]} {year}</span>
            <NavBtn dir="next" onClick={() => shiftMonth(1)} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
            {DOW.map((d, i) => (
              <span key={d} style={{ textAlign: "center", fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", fontWeight: 600, letterSpacing: "0.02em", color: i === (highlightWeekday + 6) % 7 ? "var(--accent-text)" : "var(--text-faint)", padding: "2px 0" }}>{d}</span>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {cells.map((d, i) => (
              <DayCell key={i} date={d} highlight={!!d && d.getDay() === highlightWeekday} isSelected={sameDay(d, selected)} isToday={sameDay(d, today)} onClick={() => d && choose(d)} />
            ))}
          </div>
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
              <span style={{ width: 12, height: 12, borderRadius: "50%", border: "1.5px solid var(--accent)", display: "inline-block" }} />
              Dimanche
            </span>
            <button type="button" onClick={() => { const n = nextWeekday(highlightWeekday); onChange && onChange(n); setView(parse(n)!); setOpen(false); }} style={{ border: "none", background: "transparent", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--accent-text)", padding: "2px 4px" }}>
              Prochain dimanche
            </button>
          </div>
          <style>{"@keyframes adm-pop{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}"}</style>
        </div>
      )}
    </div>
  );
}

function NavBtn({ dir, onClick }: { dir: "prev" | "next"; onClick: () => void }) {
  const [h, setH] = useState(false);
  return (
    <button type="button" onClick={onClick} aria-label={dir === "prev" ? "Mois précédent" : "Mois suivant"} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ width: 30, height: 30, display: "inline-flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", cursor: "pointer", background: h ? "var(--bg-surface)" : "transparent", color: "var(--text-secondary)", transition: "background var(--dur-fast) var(--ease-out)" }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: dir === "next" ? "rotate(180deg)" : "none" }}><path d="m15 18-6-6 6-6" /></svg>
    </button>
  );
}

function DayCell({ date, highlight, isSelected, isToday, onClick }: { date: Date | null; highlight: boolean; isSelected: boolean; isToday: boolean; onClick: () => void }) {
  const [h, setH] = useState(false);
  if (!date) return <span style={{ height: 34 }} />;
  let bg = "transparent", fg = "var(--text-primary)", border = "1px solid transparent";
  if (highlight) { border = "1.5px solid var(--accent-ring)"; fg = "var(--accent-text)"; }
  if (h) bg = "var(--accent-soft)";
  if (highlight && h) border = "1.5px solid var(--accent)";
  if (isSelected) { bg = "var(--accent)"; fg = "#fff"; border = "1.5px solid var(--accent)"; }
  return (
    <button type="button" onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ height: 34, display: "inline-flex", alignItems: "center", justifyContent: "center", position: "relative", borderRadius: "50%", border, background: bg, color: fg, cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: "var(--text-sm)", fontWeight: isSelected || highlight ? 600 : 400, transition: "background var(--dur-fast) var(--ease-out), border-color var(--dur-fast) var(--ease-out)" }}>
      {date.getDate()}
      {isToday && !isSelected && <span style={{ position: "absolute", bottom: 4, left: "50%", transform: "translateX(-50%)", width: 4, height: 4, borderRadius: "50%", background: highlight ? "var(--accent)" : "var(--text-muted)" }} />}
    </button>
  );
}
